/**
 * Portfolio panels: OpenOrdersPanel, OrderHistoryPanel, FillsPanel (#671, #672). Props: { address }.
 *
 * Outcome rows show "#<outcome> <side name>" and prices in ¢. Other markets (spot / perps) are hidden behind an
 * "Outcomes only" toggle that defaults on, unless the account has no outcome rows at all (then all are shown).
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useViewedAccount } from '../../wallet/context';
import { useCancelOrders, type CancelControls } from '../../ticket/useCancelOrders';
import type { FrontendOrder, HistoricalOrder } from '../../hl/types';
import { useFills, useOpenOrders, useOrderHistory, type FillRow } from './hooks';
import { formatPx, isOutcomeCoin, labelCoin, stripZeros, useLabelIndex, type LabelIndex } from './labels';
import { addDec, describeStatus, FILLS_PAGE_CAP, negDec, type StatusTone } from './model';
import OtherVenuesToggle from '../../venue/OtherVenuesToggle';
import { useVenueFiltered, useVenueRows, type VenueRows } from '../../venue/useVenueRows';
import { parseSettlementFill, settlementResult, type SettlementResult } from '../../settlement/parse';
import { snapshotRegistry } from '../../describe';
import { useSettlements } from '../../settlement/useSettlements';
import { SettlementFillNote } from '../../settlement/ui';
import { t as tr, type PlainKey } from '../../i18n/i18n';
import { localizeNumber } from '../../i18n/format';
import { useT } from '../../i18n/useT';

export interface PanelProps {
  address: string;
}

const HISTORY_PAGE = 50;

// ---------------------------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------------------------

function fmtTime(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function fmtNum(s: string, maxDp = 6): string {
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return localizeNumber(stripZeros(n.toFixed(maxDp)));
}

function useOutcomeFilter<T>(allRows: readonly T[], coinOf: (r: T) => string) {
  // #689: under a venue scope, outcome rows from other venues are hidden unless "Show other venues" is on.
  const venue = useVenueRows();
  const { isOtherCoin } = venue;
  const isOther = useCallback((r: T) => isOtherCoin(coinOf(r)), [isOtherCoin, coinOf]);
  const scoped = useVenueFiltered(allRows, isOther, venue);
  const rows = scoped.visible;
  const [outcomesOnly, setOutcomesOnly] = useState(true);
  const outcomeCount = useMemo(() => rows.filter((r) => isOutcomeCoin(coinOf(r))).length, [rows, coinOf]);
  const hasOutcomes = outcomeCount > 0;
  const active = outcomesOnly && hasOutcomes;
  const visible = useMemo(
    () => (active ? rows.filter((r) => isOutcomeCoin(coinOf(r))) : [...rows]),
    [rows, active, coinOf],
  );
  return {
    visible,
    active,
    hasOutcomes,
    outcomesOnly,
    setOutcomesOnly,
    hidden: rows.length - visible.length,
    venue,
    venueHidden: scoped.hidden,
  };
}

function FilterBar({
  f,
  extra,
}: {
  f: {
    active: boolean;
    hasOutcomes: boolean;
    outcomesOnly: boolean;
    setOutcomesOnly: (v: boolean) => void;
    hidden: number;
    venue: VenueRows;
    venueHidden: number;
  };
  extra?: ReactNode;
}) {
  const t = useT();
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-2 text-xs text-muted">
      <label className="flex items-center gap-1.5">
        <input
          type="checkbox"
          checked={f.hasOutcomes ? f.outcomesOnly : false}
          disabled={!f.hasOutcomes}
          onChange={(e) => f.setOutcomesOnly(e.target.checked)}
        />
        {t('orders.outcomes_only')}
      </label>
      {!f.hasOutcomes && <span>{t('orders.no_outcome_activity')}</span>}
      {f.active && f.hidden > 0 && <span>{t('orders.hidden_other', { n: f.hidden })}</span>}
      <OtherVenuesToggle v={f.venue} hidden={f.venueHidden} />
      {extra}
    </div>
  );
}

function Section({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <section className="card overflow-hidden" aria-label={title}>
      <h2 className="border-b border-border px-4 py-3 text-sm font-semibold">
        {title}
        {count !== undefined && <span className="ms-2 text-muted">({count})</span>}
      </h2>
      {children}
    </section>
  );
}

function Market({ coin, index }: { coin: string; index: LabelIndex }) {
  const l = labelCoin(coin, index);
  return (
    <div className="min-w-0">
      <div className={`truncate font-medium ${l.kind === 'outcome' ? (l.side === 0 ? 'text-yes' : 'text-no') : ''}`}>
        <span translate="no">{l.label}</span>
      </div>
      {l.title && (
        <div className="truncate text-xs text-muted" translate="no">
          {l.title}
        </div>
      )}
    </div>
  );
}

function SideCell({ side }: { side: 'B' | 'A' }) {
  const t = useT();
  return <span className={side === 'B' ? 'text-yes' : 'text-no'}>{t(side === 'B' ? 'ticket.buy' : 'ticket.sell')}</span>;
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="px-4 py-6 text-center text-sm text-muted">{children}</p>;
}

const th = 'px-3 py-2 text-start font-medium whitespace-nowrap';
const td = 'px-3 py-2 whitespace-nowrap align-top';

function Table({ head, children }: { head: (PlainKey | '')[]; children: ReactNode }) {
  const t = useT();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm tabular-nums">
        <thead className="text-xs text-muted">
          <tr>
            {head.map((h) => (
              <th key={h} className={th} scope="col">
                {h ? t(h) : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">{children}</tbody>
      </table>
    </div>
  );
}

function MoreButton({ onClick, busy, label }: { onClick: () => void; busy: boolean; label?: string }) {
  const t = useT();
  return (
    <div className="px-4 py-3 text-center">
      <button
        type="button"
        className="rounded border border-border px-3 py-1 text-xs hover:bg-bg disabled:opacity-50"
        onClick={onClick}
        disabled={busy}
      >
        {busy ? t('common.loading') : (label ?? t('orders.load_more'))}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Open orders
// ---------------------------------------------------------------------------------------------

const orderCoin = (o: FrontendOrder) => o.coin;

/** Hyperliquid order types we know; anything else is shown as Hyperliquid sends it. */
const ORDER_TYPE: Record<string, PlainKey> = {
  Limit: 'orders.type.limit',
  Market: 'ticket.tif.market',
  'Stop Market': 'orders.type.stop_market',
  'Stop Limit': 'orders.type.stop_limit',
  'Take Profit Market': 'orders.type.tp_market',
  'Take Profit Limit': 'orders.type.tp_limit',
};

function orderType(o: FrontendOrder): string {
  const tif = o.tif ? (o.tif === 'Alo' ? tr('orders.post_only') : o.tif.toUpperCase() === 'GTC' ? 'GTC' : o.tif) : null;
  const k = ORDER_TYPE[o.orderType];
  const type = k ? tr(k) : o.orderType;
  return tif ? `${type} · ${tif}` : type;
}

export interface OpenOrdersTableProps {
  orders: readonly FrontendOrder[];
  index: LabelIndex;
  /** Cancel column is rendered only when the viewer can trade (own wallet, not ghost). */
  canTrade: boolean;
  /** Cancel wiring (#678). Without it the Cancel slot renders disabled. */
  cancel?: Pick<CancelControls, 'cancel' | 'rowState' | 'disabledReason' | 'canCancelCoin' | 'busy'>;
  /** #689: rows shown via "Show other venues" are read-only (no cancel). */
  isReadOnlyCoin?: (coin: string) => boolean;
}

function CancelCell({ o, cancel, readOnly }: { o: FrontendOrder; cancel?: OpenOrdersTableProps['cancel']; readOnly?: boolean }) {
  const t = useT();
  const st = cancel?.rowState(o.oid);
  if (st?.canceling) {
    return (
      <span className="text-xs text-muted" data-testid="cancel-pending" role="status">
        {t('cancel.canceling')}
      </span>
    );
  }
  const reason = !cancel
    ? t('orders.cancel_unavailable')
    : readOnly
      ? t('orders.other_venue')
      : (cancel.disabledReason ?? (cancel.canCancelCoin(o.coin) ? null : t('orders.only_outcome')));
  return (
    <div>
      <span title={reason ?? undefined}>
        <button
          type="button"
          disabled={!!reason || cancel?.busy}
          onClick={() => cancel?.cancel([o])}
          className="rounded border border-border px-2 py-0.5 text-xs hover:border-danger hover:text-danger disabled:opacity-50 disabled:hover:border-border disabled:hover:text-inherit"
          aria-label={t('cancel.button_aria', { oid: String(o.oid) })}
        >
          {t('cancel.button')}
        </button>
      </span>
      {st?.error && (
        <div className="mt-0.5 max-w-[16rem] whitespace-normal text-xs text-danger" data-testid="cancel-error">
          {st.error}
        </div>
      )}
    </div>
  );
}

/** Presentational open-orders table (exported for tests / previews). */
export function OpenOrdersTable({ orders, index, canTrade, cancel, isReadOnlyCoin }: OpenOrdersTableProps) {
  const t = useT();
  const head: (PlainKey | '')[] = [
    'common.time',
    'common.market',
    'orders.col.side',
    'common.price',
    'orders.col.size',
    'orders.col.filled',
    'orders.col.type',
    'orders.col.reduce_only',
  ];
  if (canTrade) head.push('');
  return (
    <Table head={head}>
      {orders.map((o) => {
        const kind = labelCoin(o.coin, index).kind;
        const filled = addDec(o.origSz, negDec(o.sz));
        return (
          <tr key={o.oid} data-testid="open-order-row">
            <td className={td}>{fmtTime(o.timestamp)}</td>
            <td className={td}>
              <Market coin={o.coin} index={index} />
            </td>
            <td className={td}>
              <SideCell side={o.side} />
            </td>
            <td className={td}>{formatPx(o.limitPx, kind)}</td>
            <td className={td}>{fmtNum(o.origSz)}</td>
            <td className={td}>{fmtNum(filled)}</td>
            <td className={td}>{orderType(o)}</td>
            <td className={td}>{t(o.reduceOnly ? 'common.bool_yes' : 'common.bool_no')}</td>
            {canTrade && (
              <td className={td}>
                <CancelCell o={o} cancel={cancel} readOnly={isReadOnlyCoin?.(o.coin)} />
              </td>
            )}
          </tr>
        );
      })}
    </Table>
  );
}

export function OpenOrdersPanel({ address }: PanelProps) {
  const { orders, loading, error } = useOpenOrders(address);
  const viewed = useViewedAccount();
  const cancel = useCancelOrders();
  // Cancel only for your own connected wallet viewing its own orders (never ghost mode).
  const canTrade = viewed.canTrade && cancel.available && viewed.address?.toLowerCase() === address.toLowerCase();
  const index = useLabelIndex();
  const f = useOutcomeFilter(orders, orderCoin);
  const t = useT();
  return (
    <Section title={t('portfolio.tab.orders')} count={f.visible.length}>
      <FilterBar f={f} />
      {canTrade && cancel.notice && (
        <p role="status" className="px-4 py-1 text-xs text-warn" data-testid="cancel-notice">
          {cancel.notice}
        </p>
      )}
      {canTrade && cancel.dialog}
      {error && <p className="px-4 py-2 text-xs text-danger">{t('orders.open.error', { error })}</p>}
      {loading ? (
        <Empty>{t('orders.open.loading')}</Empty>
      ) : f.visible.length === 0 ? (
        <Empty>{t('orders.open.none')}</Empty>
      ) : (
        <OpenOrdersTable
          orders={f.visible}
          index={index}
          canTrade={canTrade}
          cancel={cancel}
          isReadOnlyCoin={f.venue.isOtherCoin}
        />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------------------------
// Order history
// ---------------------------------------------------------------------------------------------

const historyCoin = (h: HistoricalOrder) => h.order.coin;

const TONE: Record<StatusTone, string> = {
  open: 'text-fg',
  filled: 'text-yes',
  canceled: 'text-muted',
  rejected: 'text-danger',
  other: 'text-fg',
};

export function OrderHistoryPanel({ address }: PanelProps) {
  const { history, loading, complete, error } = useOrderHistory(address);
  const index = useLabelIndex();
  const f = useOutcomeFilter(history, historyCoin);
  const t = useT();
  const [shown, setShown] = useState(HISTORY_PAGE);
  const rows = f.visible.slice(0, shown);
  const hasMore = f.visible.length > shown;
  return (
    <Section title={t('portfolio.tab.history')} count={f.visible.length}>
      <FilterBar f={f} />
      {error && <p className="px-4 py-2 text-xs text-danger">{t('orders.history.error', { error })}</p>}
      {loading || (history.length === 0 && !complete && !error) ? (
        <Empty>{t('orders.history.loading')}</Empty>
      ) : rows.length === 0 ? (
        <Empty>{t('orders.history.none')}</Empty>
      ) : (
        <>
          <Table
            head={[
              'common.time',
              'common.market',
              'orders.col.side',
              'common.price',
              'orders.col.size',
              'orders.col.filled',
              'orders.col.type',
              'orders.col.status',
            ]}
          >
            {rows.map((h) => {
              const o = h.order;
              const kind = labelCoin(o.coin, index).kind;
              const st = describeStatus(h.status);
              return (
                <tr key={`${o.oid}:${h.status}`} data-testid="history-row">
                  <td className={td}>{fmtTime(h.statusTimestamp)}</td>
                  <td className={td}>
                    <Market coin={o.coin} index={index} />
                  </td>
                  <td className={td}>
                    <SideCell side={o.side} />
                  </td>
                  <td className={td}>{formatPx(o.limitPx, kind)}</td>
                  <td className={td}>{fmtNum(o.origSz)}</td>
                  <td className={td}>{fmtNum(addDec(o.origSz, negDec(o.sz)))}</td>
                  <td className={td}>{orderType(o)}</td>
                  <td className={td}>
                    <span className={TONE[st.tone]}>{st.label}</span>
                    {st.reason && <div className="text-xs text-muted">{st.reason}</div>}
                  </td>
                </tr>
              );
            })}
          </Table>
          {(hasMore || !complete) && <MoreButton onClick={() => setShown((n) => n + HISTORY_PAGE)} busy={!hasMore} />}
        </>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------------------------
// Fills
// ---------------------------------------------------------------------------------------------

const fillCoin = (r: FillRow) => r.coin;

function signedClass(s: string): string {
  const n = Number(s);
  return n > 0 ? 'text-yes' : n < 0 ? 'text-no' : '';
}

/** Settlement fill not (yet) in the settlement store: the result from the fill alone (payout = px). */
function settlementOf(r: FillRow, index: LabelIndex): SettlementResult | undefined {
  const f = parseSettlementFill(r);
  return f ? settlementResult(f, null, { templates: index.templates ?? snapshotRegistry() }) : undefined;
}

function SettlementMarket({ r }: { r: SettlementResult }) {
  return (
    <div className="min-w-0">
      <div className={`truncate font-medium ${r.side === 0 ? 'text-yes' : 'text-no'}`} translate="no">
        #{r.outcome} {r.sideName}
      </div>
      <div className="truncate text-xs text-muted" translate="no">
        {r.title}
      </div>
    </div>
  );
}

export function FillsTable({
  fills,
  index,
  settlements,
}: {
  fills: readonly FillRow[];
  index: LabelIndex;
  /** #788: settlement results by fill tid; Settlement rows get the market name and result from here. */
  settlements?: ReadonlyMap<number, SettlementResult>;
}) {
  return (
    <Table
      head={[
        'common.time',
        'common.market',
        'orders.col.side',
        'common.price',
        'orders.col.size',
        'orders.col.fee',
        'orders.col.closed_pnl',
        'orders.col.position_after',
      ]}
    >
      {fills.map((r) => {
        const kind = labelCoin(r.coin, index).kind;
        const st = settlements?.get(r.tid) ?? settlementOf(r, index);
        return (
          <tr key={r.tid} data-testid="fill-row">
            <td className={td}>{fmtTime(r.time)}</td>
            <td className={td}>
              {st && !index.outcomes.has(st.outcome) ? <SettlementMarket r={st} /> : <Market coin={r.coin} index={index} />}
            </td>
            <td className={td}>
              <SideCell side={r.side} />
              {r.dir && r.dir !== 'Buy' && r.dir !== 'Sell' && (
                <div className="text-xs text-muted" translate="no">
                  {r.dir}
                </div>
              )}
              {st && <SettlementFillNote r={st} />}
            </td>
            <td className={td}>{formatPx(r.px, kind)}</td>
            <td className={td}>{fmtNum(r.sz)}</td>
            <td className={td}>
              {fmtNum(r.fee)} <span translate="no">{r.feeToken}</span>
            </td>
            <td className={`${td} ${signedClass(r.closedPnl)}`}>{fmtNum(r.closedPnl)}</td>
            <td className={td} title={`${fmtNum(r.startPosition)} → ${fmtNum(r.positionAfter)}`}>
              {fmtNum(r.positionAfter)}
            </td>
          </tr>
        );
      })}
    </Table>
  );
}

export function FillsPanel({ address }: PanelProps) {
  const { fills, loading, complete, capped, error } = useFills(address);
  const index = useLabelIndex();
  const f = useOutcomeFilter(fills, fillCoin);
  const [shown, setShown] = useState(HISTORY_PAGE);
  const rows = f.visible.slice(0, shown);
  const hasMore = f.visible.length > shown;
  const settled = useSettlements(address).results;
  const settlements = useMemo(() => new Map(settled.map((s) => [s.tid, s])), [settled]);
  const t = useT();
  return (
    <Section title={t('orders.fills.title')} count={f.visible.length}>
      <FilterBar f={f} />
      {error && <p className="px-4 py-2 text-xs text-danger">{t('orders.fills.error', { error })}</p>}
      {loading ? (
        <Empty>{t('orders.fills.loading')}</Empty>
      ) : (
        <>
          {rows.length === 0 ? (
            <Empty>{t(complete ? 'orders.fills.none' : 'orders.fills.none_yet')}</Empty>
          ) : (
            <FillsTable fills={rows} index={index} settlements={settlements} />
          )}
          {(hasMore || !complete) && (
            <MoreButton
              onClick={() => setShown((n) => n + HISTORY_PAGE)}
              busy={!hasMore}
              label={t('orders.load_older_fills')}
            />
          )}
          {capped && !hasMore && (
            <p className="px-4 py-2 text-xs text-muted" data-testid="fills-capped">
              {t('orders.fills.capped_note', { limit: FILLS_PAGE_CAP })}
            </p>
          )}
        </>
      )}
    </Section>
  );
}
